import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDesignStore from '../../store/designStore';
import useProjectStore from '../../store/projectStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';

const initialForm = {
  project_id: '',
  design_flow_m3hr: '',
  number_of_vessels: '1',
  vessel_construction: 'frp',
  max_service_velocity_m_hr: '15',
  carbon_bed_depth_mm: '600',
  target_ebct_min: '10',
  carbon_bulk_density_kg_m3: '480',
  freeboard_percent: '50',
  backwash_rate_m_hr: '30',
  backwash_duration_min: '10',
  feed_pipe_velocity_m_s: '2.5',
};

const ACFCalculatorPage = () => {
  const [form, setForm] = useState(initialForm);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const { result, isCalculating, isSubmitting, error, calculate, saveCalculation, clearResult } = useDesignStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    clearResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const buildInputs = () => {
    if (!form.design_flow_m3hr) {
      showToast('Design flow is required', 'error');
      return null;
    }
    return {
      design_flow_m3hr: Number(form.design_flow_m3hr),
      number_of_vessels: Number(form.number_of_vessels),
      vessel_construction: form.vessel_construction,
      max_service_velocity_m_hr: Number(form.max_service_velocity_m_hr),
      carbon_bed_depth_mm: Number(form.carbon_bed_depth_mm),
      target_ebct_min: Number(form.target_ebct_min),
      carbon_bulk_density_kg_m3: Number(form.carbon_bulk_density_kg_m3),
      freeboard_percent: Number(form.freeboard_percent),
      backwash_rate_m_hr: Number(form.backwash_rate_m_hr),
      backwash_duration_min: Number(form.backwash_duration_min),
      feed_pipe_velocity_m_s: Number(form.feed_pipe_velocity_m_s),
    };
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    const inputs = buildInputs();
    if (!inputs) return;
    await calculate('acf', inputs);
  };

  const handleSave = async () => {
    if (!result) return;
    const payload = {
      calculator_type: 'acf',
      project_id: form.project_id ? Number(form.project_id) : null,
      created_by_id: user?.id || null,
      client_data: result.inputs,
      calculation_outputs: result.outputs,
    };
    const saveResult = await saveCalculation(payload);
    if (saveResult.success) {
      showToast('Calculation saved as draft', 'success');
      navigate('/design/calculations');
    } else {
      showToast(saveResult.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-1">Activated Carbon Filter (ACF)</h1>
        <p className="text-xs text-gray-500 mb-6">
          Vessel diameter sized from flow ÷ max velocity, like PSF; carbon bed depth is a fixed
          600mm standard. EBCT is a check on the result, not a sizing input — flagged if contact
          time falls short. No ASME shell/head thickness calc here — see "Assumptions" below.
        </p>

        <div className="glass-card p-6">
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
              <select
                value={form.project_id}
                onChange={handleChange('project_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Not linked to a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Design Flow (m³/hr) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.design_flow_m3hr}
                  onChange={handleChange('design_flow_m3hr')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1"># of Vessels</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.number_of_vessels}
                  onChange={handleChange('number_of_vessels')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vessel Construction</label>
                <select
                  value={form.vessel_construction}
                  onChange={handleChange('vessel_construction')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="frp">FRP (standard sizes)</option>
                  <option value="ms_fabricated">MS (fabricated, round to 50mm)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Service Velocity (m/hr)</label>
                <input
                  type="number"
                  step="any"
                  value={form.max_service_velocity_m_hr}
                  onChange={handleChange('max_service_velocity_m_hr')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Carbon Bed Depth (mm)</label>
                <input
                  type="number"
                  step="any"
                  value={form.carbon_bed_depth_mm}
                  onChange={handleChange('carbon_bed_depth_mm')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Default: 600 (fixed standard)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target EBCT (min, check only)</label>
                <input
                  type="number"
                  step="any"
                  value={form.target_ebct_min}
                  onChange={handleChange('target_ebct_min')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-xs font-semibold text-komal-blue hover:underline"
              >
                {showAdvanced ? '− Hide' : '+ Show'} carbon &amp; backwash parameters (defaults: 480 kg/m³ bulk density / 50% freeboard / 30 m/hr backwash / 10 min / 2.5 m/s feed pipe)
              </button>

              {showAdvanced && (
                <div className="grid grid-cols-3 gap-4 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Carbon Bulk Density (kg/m³)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.carbon_bulk_density_kg_m3}
                      onChange={handleChange('carbon_bulk_density_kg_m3')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Freeboard (% of bed depth)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.freeboard_percent}
                      onChange={handleChange('freeboard_percent')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Backwash Rate (m/hr)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.backwash_rate_m_hr}
                      onChange={handleChange('backwash_rate_m_hr')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Backwash Duration (min)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.backwash_duration_min}
                      onChange={handleChange('backwash_duration_min')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Feed Pipe Velocity (m/s)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.feed_pipe_velocity_m_s}
                      onChange={handleChange('feed_pipe_velocity_m_s')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="submit"
                disabled={isCalculating}
                className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
              >
                {isCalculating ? 'Calculating...' : 'Calculate'}
              </button>
            </div>
          </form>
        </div>

        {result && (
          <div className="glass-card p-6 mt-4">
            <h2 className="font-semibold text-gray-900 mb-4">Result</h2>

            {result.outputs.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 space-y-1">
                {result.outputs.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-800">⚠ {w}</p>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <Stat label="Flow per Vessel" value={`${result.outputs.flow_per_vessel_m3hr} m³/hr`} />
              <Stat label="Diameter (calculated)" value={`${result.outputs.diameter_required_mm} mm`} />
              <Stat
                label="Diameter (selected)"
                value={`${result.outputs.diameter_recommended_mm} mm (${result.outputs.diameter_recommended_inches})`}
                highlight
              />
              <Stat label="Actual Velocity" value={`${result.outputs.actual_velocity_m_hr} m/hr`} />
              <Stat label="Carbon Bed Depth (fixed)" value={`${result.outputs.bed_depth_mm} mm`} />
              <Stat label="Actual EBCT (check)" value={`${result.outputs.actual_ebct_min} min`} />
              <Stat label="Freeboard" value={`${result.outputs.freeboard_mm} mm`} />
              <Stat label="Required Internal Height" value={`${result.outputs.required_internal_height_mm} mm`} />
              <Stat
                label={result.outputs.height_recommended_mm != null ? 'Vessel Height (catalog)' : 'Vessel Height (fabricated)'}
                value={
                  result.outputs.height_recommended_inches
                    ? `${result.outputs.overall_height_mm} mm (${result.outputs.height_recommended_inches})`
                    : `${result.outputs.overall_height_mm} mm`
                }
                highlight={result.outputs.vessel_fits}
              />
              {!result.outputs.vessel_fits && (
                <Stat label="Fits Catalog?" value="No — see warning above" highlight />
              )}
              <Stat label="Carbon Weight (total)" value={`${result.outputs.total_carbon_weight_kg.toLocaleString()} kg`} highlight />
              <Stat label="Backwash Flow" value={`${result.outputs.backwash_flow_m3hr} m³/hr`} />
              <Stat label="Backwash Volume" value={`${result.outputs.backwash_volume_liters.toLocaleString()} L`} />
              <Stat
                label={`Feed Pipe Diameter (@ ${result.outputs.feed_pipe_velocity_m_s} m/s)`}
                value={`${result.outputs.feed_pipe_diameter_mm} mm (${result.outputs.feed_pipe_diameter_inches})`}
              />
            </div>

            {(result.outputs.calculation_steps || result.outputs.assumptions) && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className="text-xs font-semibold text-komal-blue hover:underline mb-3"
                >
                  {showDetails ? '− Hide calculation & assumptions' : '+ Show calculation & assumptions'}
                </button>

                {showDetails && (
                  <div className="space-y-4">
                    {result.outputs.calculation_steps?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Calculation Steps
                        </h3>
                        <div className="bg-gray-50 rounded border border-gray-100">
                          <ScrollX>
                          <table className="w-full text-xs">
                            <tbody>
                              {result.outputs.calculation_steps.map((step, i) => (
                                <tr key={i} className="border-b border-gray-100 last:border-0">
                                  <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">{step.step}</td>
                                  <td className="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">{step.formula}</td>
                                  <td className="px-3 py-2 text-gray-900 font-mono">{step.result}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </ScrollX>
                        </div>
                      </div>
                    )}

                    {result.outputs.assumptions?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Assumptions
                        </h3>
                        <ul className="list-disc list-inside space-y-1">
                          {result.outputs.assumptions.map((a, i) => (
                            <li key={i} className="text-xs text-gray-600">{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={() => clearResult()}
                className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
              >
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`font-semibold ${highlight ? 'text-komal-blue text-lg' : 'text-gray-900'}`}>{value}</div>
  </div>
);

export default ACFCalculatorPage;
